import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";
import { getStaffAccessToken } from "@dashboard/services/saleorStaffToken";
import { useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";

import { prepareCheckoutMessages as messages } from "./messages";

/** The Quotes app's own amount rule: positive, at most 10 integer digits and 2 decimals. */
const FREIGHT_AMOUNT_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;

export const isValidFreightAmount = (value: string): boolean =>
  FREIGHT_AMOUNT_PATTERN.test(value) && Number(value) > 0;

/** 200 body of `POST /api/admin/prepared/freight` (wsm-app-platform, external_apps/quotes). */
export interface PreparedFreightResponse {
  checkout_id: string;
  freight_quoted: string | null;
  quote_id: number | null;
}

const errorMessage = (status: number, code?: string): MessageDescriptor => {
  if (status === 400) return messages.freightBadAmount;

  if (status === 404) return messages.freightNotFound;

  if (status === 409 && code === "freight_locked") return messages.freightLocked;

  if (status === 409 && code === "not_prepared") return messages.freightNotPrepared;

  if (status === 502) return messages.freightUnreachable;

  return messages.freightFailed;
};

export interface UsePreparedFreight {
  /** The amount on the cart as the Quotes app last reported it, or null for none. */
  amount: string | null;
  saving: boolean;
  error: string | null;
  /** Each resolves true once the Quotes app has taken the change. */
  save: (amount: string) => Promise<boolean>;
  clear: () => Promise<boolean>;
}

/**
 * The rep's quoted freight on a prepared cart. The Quotes app owns the value (it
 * lives in the cart's private metadata, where only its shipping webhook reads
 * it), so the dashboard only ever sets or clears it there and shows what came back.
 *
 * ponytail: the amount starts empty because the only cart this reaches is one
 * this tab just prepared, which has none. Ceiling: a reload loses the cart.
 * Upgrade path: a read on the same route, once a cart can be reopened here.
 */
export const usePreparedFreight = (checkoutId: string): UsePreparedFreight => {
  const intl = useIntl();
  const [amount, setAmount] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (next: string | null): Promise<boolean> => {
    if (next !== null && !isValidFreightAmount(next)) {
      setError(intl.formatMessage(messages.freightBadAmount));

      return false;
    }

    setSaving(true);
    setError(null);

    try {
      const token = await getStaffAccessToken();
      const response = await fetch(`${getAppPlatformUrl("quotes")}/api/admin/prepared/freight`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "X-Saleor-Api-Url": getApiUrl(),
        },
        body: JSON.stringify({ checkout_id: checkoutId, amount: next }),
      });
      const body = await response.json().catch(() => null);

      if (response.ok) {
        setAmount((body as PreparedFreightResponse | null)?.freight_quoted ?? null);

        return true;
      }

      setError(intl.formatMessage(errorMessage(response.status, body?.code)));

      return false;
    } catch {
      setError(intl.formatMessage(messages.freightFailed));

      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    amount,
    saving,
    error,
    save: value => submit(value.trim()),
    clear: () => submit(null),
  };
};
